// src/components/Admin/TaskManager/QueueStatusPanel.tsx

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { useQueueStatus } from '../../../hooks/useQueueStatus';
import { useTaskStatistics } from '../../../hooks/useScheduledTasks';

interface QueueInfo {
  name: string;
  messages: number;
  consumers: number;
  error?: string;
}

const QueueStatusPanel: React.FC = () => {
  const {
    status: queueStatus,
    loading: queueLoading,
    error: queueError,
    refetch: refetchQueue,
  } = useQueueStatus();

  const {
    statistics: stats,
    loading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useTaskStatistics();

  const handleRefresh = () => {
    refetchStats();
    refetchQueue();
  };

  const getQueueHealth = (queue: QueueInfo) => {
    // Check for queue errors first
    if (queue.error) {
      return {
        status: 'error',
        icon: <ErrorIcon fontSize="small" />,
        text: 'Error'
      };
    }

    if (queue.consumers === 0 && queue.messages > 0) {
      return {
        status: 'error',
        icon: <ErrorIcon fontSize="small" />,
        text: 'No workers!'
      };
    }

    if (queue.messages > 10) {
      return {
        status: 'warning',
        icon: <WarningIcon fontSize="small" />,
        text: 'Queue building'
      };
    }

    return {
      status: 'success',
      icon: <CheckCircleIcon fontSize="small" />,
      text: 'Healthy'
    };
  };

  // Determine if we should use compact (horizontal) or scrollable (vertical) layout
  const queueCount = queueStatus?.queues?.length || 0;
  const useCompactLayout = queueCount <= 3;

  // Check for critical issues
  const hasNoWorkers = queueStatus?.queues?.some(
    (q: QueueInfo) => q.consumers === 0 && q.messages > 0
  );
  const hasHighQueueDepth = queueStatus?.queues?.some(
    (q: QueueInfo) => q.messages > 20
  );

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">System Status</Typography>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          variant="outlined"
          disabled={queueLoading || statsLoading}
        >
          Refresh
        </Button>
      </Box>

      <Grid container spacing={2}>
        {/* Scheduled Tasks Stats */}
        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{
              height: '100%',
              minHeight: 180,
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Scheduled Tasks
              </Typography>

              {statsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : statsError ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
                  <Alert severity="error" sx={{ width: '100%' }}>
                    Failed to load statistics
                  </Alert>
                </Box>
              ) : stats ? (
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={4}>
                    <Typography variant="h4">{stats.total}</Typography>
                    <Typography variant="caption" color="text.secondary">Total</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h4" color="success.main">{stats.active}</Typography>
                    <Typography variant="caption" color="text.secondary">Active</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h4" color="primary.main">{stats.due_in_next_hour}</Typography>
                    <Typography variant="caption" color="text.secondary">Due Soon</Typography>
                  </Grid>

                  {/* Additional stats in a second row */}
                  <Grid item xs={4}>
                    <Typography variant="body2" color="warning.main">{stats.paused}</Typography>
                    <Typography variant="caption" color="text.secondary">Paused</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="error.main">{stats.disabled}</Typography>
                    <Typography variant="caption" color="text.secondary">Disabled</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">{stats.expired}</Typography>
                    <Typography variant="caption" color="text.secondary">Expired</Typography>
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
                  <Typography variant="body2" color="text.secondary">No data</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Queue Status */}
        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{
              height: '100%',
              minHeight: 180,
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                RabbitMQ Queues
              </Typography>

              {queueLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : queueError ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
                  <Alert severity="error" sx={{ width: '100%' }}>
                    Failed to connect to RabbitMQ
                  </Alert>
                </Box>
              ) : queueStatus && queueStatus.queues && queueStatus.queues.length > 0 ? (
                useCompactLayout ? (
                  /* ✅ Compact Horizontal Layout (≤3 queues) */
                  <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    {queueStatus.queues.map((queue: QueueInfo) => {
                      const health = getQueueHealth(queue);
                      return (
                        <Grid item xs={12 / queueCount} key={queue.name}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              p: 1.5,
                              bgcolor: 'background.default',
                              borderRadius: 1,
                              height: '100%',
                              minHeight: 100,
                            }}
                          >
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              align="center"
                              title={queue.name}
                              sx={{
                                mb: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                width: '100%',
                              }}
                            >
                              {queue.name}
                            </Typography>

                            <Typography variant="h5" sx={{ my: 0.5 }}>
                              {queue.messages}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                              message{queue.messages !== 1 ? 's' : ''}
                            </Typography>

                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                              {queue.consumers} worker{queue.consumers !== 1 ? 's' : ''}
                            </Typography>

                            <Chip
                              icon={health.icon}
                              label={health.text}
                              size="small"
                              color={health.status as any}
                              sx={{
                                fontSize: '0.7rem',
                                height: 24,
                              }}
                            />
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  /* ✅ Scrollable Vertical Layout (>3 queues) */
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      maxHeight: 120,
                      overflowY: 'auto',
                      pr: 0.5,
                      mt: 0.5,
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '3px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: '#555',
                      },
                    }}
                  >
                    {queueStatus.queues.map((queue: QueueInfo) => {
                      const health = getQueueHealth(queue);
                      return (
                        <Box
                          key={queue.name}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 1,
                            bgcolor: 'background.default',
                            borderRadius: 1,
                            minHeight: 52,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              title={queue.name}
                              noWrap
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {queue.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {queue.messages} msg • {queue.consumers} worker{queue.consumers !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                          <Chip
                            icon={health.icon}
                            label={health.text}
                            size="small"
                            color={health.status as any}
                            sx={{
                              ml: 1,
                              minWidth: 90,
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
                  <Typography variant="body2" color="text.secondary">No queue data</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts - Fixed positioning */}
      <Box sx={{ mt: 2 }}>
        {hasNoWorkers && (
          <Alert severity="error" sx={{ mb: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              ⚠️ No workers connected to queue!
            </Typography>
            <Typography variant="caption">
              Messages are queued but not being processed. Workers may be down or misconfigured.
            </Typography>
          </Alert>
        )}

        {hasHighQueueDepth && (
          <Alert severity="warning">
            <Typography variant="body2">
              Queue depth is high. Consider scaling workers or investigating stuck jobs.
            </Typography>
          </Alert>
        )}

        {queueStatus?.queues?.some((q: QueueInfo) => q.error) && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="body2">
              Some queues could not be reached. Check RabbitMQ connection.
            </Typography>
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export default QueueStatusPanel;